import * as React from "react";
import { formatText } from "bernie-l10n";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { inject, observer } from "mobx-react";
import { observable } from "mobx";

import { UitkLink } from "uitk-react-link";
import { AnalyticsConfig } from "uitk-react-utils";
import {
  UitkCard,
  UitkCardContent,
  UitkCardTextSegment,
  UitkCardSectionSeparatorType
} from "uitk-react-cards";
import { UitkIcon } from "uitk-react-icons";

import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";

import { JumplinkProps, JumplinksProps, JumplinksViewProps } from "./typings";
import { Geo } from "typings/flexFramework/FlexDefinitions";

enum GeoType {
  MULTI_CITY = "multi_city_vicinity",
  CITY = "city"
}

enum TravelGuideLinkType {
  SPOKE_PAGE = "spokePage",
  NEIGHBORHOOD_PAGE = "neighborhoodPage"
}

@inject("dx", "analytics")
@observer
export class Jumplinks extends React.Component<JumplinksProps> {
  private readonly jumplinksRef: React.RefObject<HTMLDivElement>;

  @observable
  private activeJumplinkIdentifier: number;

  constructor(props: JumplinksProps) {
    super(props);
    this.jumplinksRef = React.createRef();
  }

  /* istanbul ignore next line */
  public componentDidMount() {
    window.addEventListener("load", () => {
      const jumplinkPositionCode = location.href.split("#", 2)[1];
      // Assuming that there are no other '#' values in the URL, also no other parameters are following the jumplink
      if (jumplinkPositionCode && this.props.dx) {
        const selectedJumplink = this.props.dx.jumplinksArray.filter(current => {
          if (current.localisedName) {
            return jumplinkPositionCode === current.localisedName.replace(/ /g, "-").toLowerCase();
          }

          return false;
        });
        if (selectedJumplink[0].reference) {
          this.scrollTo(selectedJumplink[0].reference.current, "large");
        }
      }
    });
  }

  private getJumplinkUrl = (name: string) => {
    const url = location.href.split("#")[0];

    return `${url}#${name.replace(/ /g, "-").toLowerCase()}`;
  };

  /* istanbul ignore next line */
  private handleClick = (
    screenSize: string,
    reference: React.RefObject<HTMLDivElement>,
    name?: string
  ) => (event: React.MouseEvent<HTMLButtonElement>) => {
    const fallbackElement: HTMLElement | null = reference && reference.current;

    let elementToScrollTo: HTMLElement | null = null;

    if (name) {
      location.href = this.getJumplinkUrl(name);

      const elementWithId: HTMLElement | null = document.getElementById(name);
      elementToScrollTo = elementWithId || fallbackElement;
    } else {
      elementToScrollTo = fallbackElement;
    }

    this.scrollTo(elementToScrollTo, screenSize);

    return event;
  };

  /* istanbul ignore next line */
  private scrollTo = (element: HTMLElement | null, screenSize: string): void => {
    if (!element) {
      return;
    }

    // TODO: clean up when wizard experiment is finished
    const { flexComponent } = this.props;
    const model = flexComponent && flexComponent.model;
    const isDxWizardExperiment =
      model && model.experimentName === "Compass_Wizard_DX_Pages" && model.experimentBucket === 1;

    let wizardHeight: number = 0;

    if (isDxWizardExperiment && screenSize === "large") {
      const wizardElement = document.getElementById("wizard-hotel-pwa-1");

      if (wizardElement) {
        wizardHeight = wizardElement.offsetHeight;
      }
    }

    const offsetPosition = element && element.offsetTop;

    if (offsetPosition) {
      window.scrollTo({
        top: offsetPosition - wizardHeight,
        behavior: "smooth"
      });
    } else if (element) {
      element.scrollIntoView();
    }
  };

  private jumplinksTitle = () => {
    const { flexComponent, context } = this.props;

    const model = flexComponent && flexComponent.model;
    const title = model && model.title;
    const location = context && context.searchContext && context.searchContext.location;
    const locationName =
      location && (location.localizedName || location.defaultName || location.name);

    if (!(title || locationName)) {
      return null;
    }

    return (
      <h3 className="uitk-type-heading-500 all-b-margin-one">
        {title || formatText("jumplinks.discover", locationName)}
      </h3>
    );
  };

  private isSupportedTravelGuideLink = (travelGuideLink?: string): boolean => {
    if (!travelGuideLink) {
      return false;
    }

    return (
      travelGuideLink === TravelGuideLinkType.SPOKE_PAGE ||
      travelGuideLink === TravelGuideLinkType.NEIGHBORHOOD_PAGE
    );
  };

  private getLocationBasedOnPageType = (location: Geo, travelGuideLink?: string): Geo | null => {
    if (!travelGuideLink) {
      return null;
    }

    switch (travelGuideLink) {
      case TravelGuideLinkType.SPOKE_PAGE:
        return location;
      case TravelGuideLinkType.NEIGHBORHOOD_PAGE:
        const parentLocation = location.parent;
        const parentLocationType =
          parentLocation && parentLocation.realWorldGeo && parentLocation.realWorldGeo.type;

        const isCityType =
          parentLocationType === GeoType.CITY || parentLocationType === GeoType.MULTI_CITY;

        if (!isCityType) {
          return null;
        }

        return parentLocation;
      default:
        return null;
    }
  };

  private travelGuideLinkView = () => {
    const { flexComponent, context } = this.props;

    const model = flexComponent && flexComponent.model;
    const travelGuideLink = model && model.travelGuideLink;
    const location = context && context.searchContext && context.searchContext.location;

    if (!(this.isSupportedTravelGuideLink(travelGuideLink) && location)) {
      return null;
    }

    const locationToUse = this.getLocationBasedOnPageType(location, travelGuideLink);
    const locationId = locationToUse && locationToUse.id;
    const locationName = locationToUse && locationToUse.defaultName;
    const locationLocalizedName = locationToUse && locationToUse.localizedName;

    if (!(locationId && locationName && locationLocalizedName)) {
      return null;
    }

    return (
      <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP}>
        <UitkLink>
          <TrackedLink
            moduleName="TG.LP.Dest.Jumplinks"
            rfrr="city.travel.guide.link"
            href={`/${locationName.replace(" ", "-")}.dx${locationId}`}
            linkName="RFRR Action Link"
          >
            <UitkIcon name="arrow_back" />
            <span>{formatText("jumplinks.cityTravelGuideLink", locationLocalizedName)}</span>
          </TrackedLink>
        </UitkLink>
      </UitkCardTextSegment>
    );
  };

  public render() {
    const { flexComponent, context, dx } = this.props;
    const keyHelper = new ItemKeyHelper("jumplinks");

    return (
      <div className="uitk-cell all-cell-1-1">
        <UitkCard className="uitk-flat-border-top">
          <UitkCardContent>
            <UitkCardTextSegment>
              {this.jumplinksTitle()}

              <div
                id={flexComponent.id}
                data-testid="jumplinks"
                className="jumplinks"
                ref={this.jumplinksRef}
              >
                <JumplinksView
                  key={keyHelper.next()}
                  flexComponent={flexComponent}
                  context={context}
                  jumplinksRef={this.jumplinksRef}
                  dx={dx}
                  keyHelper={keyHelper}
                  handleClick={this.handleClick}
                  getJumplinkUrl={this.getJumplinkUrl}
                  activeJumplinkIdentifier={this.activeJumplinkIdentifier}
                  isFloating={false}
                />
              </div>
            </UitkCardTextSegment>

            {this.travelGuideLinkView()}
          </UitkCardContent>
        </UitkCard>
      </div>
    );
  }
}

/* istanbul ignore next line */
const JumplinksView = observer((props: JumplinksViewProps) => {
  const { dx, getJumplinkUrl } = props;

  let sortedJumplinks: JumplinkItem[] = [];
  if (dx) {
    sortedJumplinks = [...dx.jumplinksArray.sort((a, b) => a.identifier - b.identifier)];
  }

  return (
    <Viewport>
      <ViewSmall>
        <UitkCard>
          <nav className="jumplinks-mobile uitk-flex uitk-flex-column">
            {sortedJumplinks.map((jumplink: JumplinkItem) => (
              <Jumplink
                key={props.keyHelper.next()}
                jumplink={jumplink}
                handleClick={props.handleClick}
                getJumplinkUrl={getJumplinkUrl}
                screenSize="small"
              />
            ))}
          </nav>
        </UitkCard>
      </ViewSmall>

      <ViewLarge>
        <nav className="jumplinks-desktop all-t-margin-zero all-t-margin-zero uitk-flex uitk-flex-column">
          {sortedJumplinks.map((jumplink: JumplinkItem) => (
            <Jumplink
              key={props.keyHelper.next()}
              jumplink={jumplink}
              handleClick={props.handleClick}
              getJumplinkUrl={getJumplinkUrl}
              screenSize="large"
            />
          ))}
        </nav>
      </ViewLarge>
    </Viewport>
  );
});

/* istanbul ignore next line */
const Jumplink = inject("analytics")((props: JumplinkProps) => {
  const { jumplink, getJumplinkUrl, screenSize } = props;

  const analytics: AnalyticsConfig = {
    id: `TG.LP.Dest.JumpTo.jumplinks.${jumplink.name}`,
    callback: (id, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: id,
        action: Action.CLICK,
        linkName: description
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    }
  };

  return (
    <UitkLink
      className="uitk-type-left all-y-padding-two"
      onClick={props.handleClick(screenSize, jumplink.reference, jumplink.localisedName)}
      aria-label={jumplink.name}
      analytics={analytics}
      inline={true}
    >
      <a tabIndex={0} href={getJumplinkUrl(jumplink.name)}>
        {jumplink.localisedName}
      </a>
    </UitkLink>
  );
});

export interface JumplinkItem {
  identifier: number;
  name: string;
  icon?: string;
  reference: React.RefObject<HTMLDivElement>;
  localisedName?: string;
}

(JumplinksView as React.SFC).displayName = "JumplinksView";

export default Jumplinks;
